import { RouteSegment } from '@core/enums/route-segment.enum';

import { PermissionStoreModelEnum } from '@domain/permissions/permissions.enums';
import { FeatureFlags } from '@domain/tenant/tenant.enums';

export type QuickNavigationScope = 'global' | 'site';

export interface QuickNavigationRouteDefinition {
  labelTranslationKey: string;
  scope: QuickNavigationScope;
  segments: RouteSegment[];
  permissions?: PermissionStoreModelEnum[];
  additionalPermissionGroups?: PermissionStoreModelEnum[][];
  featureFlags?: FeatureFlags[];
  requiresDashboardEnabled?: boolean;
  requiresGlobalChargersEnabled?: boolean;
  requiresGlobalVehiclesEnabled?: boolean;
  requiresDesktop?: boolean;
  requiresTelemetryFeaturesEnabled?: boolean;
  requiresFleetSite?: boolean;
  requiresGridViewEnabled?: boolean;
  requiresOptimoTenant?: boolean;
}

export const quickNavigationRoutes = {
  dashboard: {
    labelTranslationKey: 'side-menu.dashboard',
    scope: 'global',
    segments: [RouteSegment.Root],
    permissions: [PermissionStoreModelEnum.ViewAnyDashboard, PermissionStoreModelEnum.ViewOwnDashboard],
    requiresDashboardEnabled: true,
    requiresDesktop: true,
  },
  sites: { labelTranslationKey: 'side-menu.sites', scope: 'global', segments: [RouteSegment.Root, RouteSegment.Sites] },
  'id-tags': {
    labelTranslationKey: 'side-menu.id-tags',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.IdTags],
    permissions: [PermissionStoreModelEnum.ViewAnyIdTag, PermissionStoreModelEnum.ViewOwnIdTag],
  },
  alerting: { labelTranslationKey: 'side-menu.alerting', scope: 'global', segments: [RouteSegment.Root, RouteSegment.Alerting] },
  reports: {
    labelTranslationKey: 'side-menu.reports',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.Reports],
    permissions: [PermissionStoreModelEnum.ViewAnyReports, PermissionStoreModelEnum.ViewOwnReports],
  },
  vehicles: {
    labelTranslationKey: 'side-menu.vehicles',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.Vehicles],
    permissions: [PermissionStoreModelEnum.ViewAnyVehicles, PermissionStoreModelEnum.ViewOwnVehicles],
    requiresGlobalVehiclesEnabled: true,
  },
  'vehicle-trips': {
    labelTranslationKey: 'vehicles.trips.tab-title',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.Vehicles, RouteSegment.Trip],
    permissions: [PermissionStoreModelEnum.ViewAnyVehicles, PermissionStoreModelEnum.ViewOwnVehicles],
    requiresGlobalVehiclesEnabled: true,
    requiresTelemetryFeaturesEnabled: true,
  },
  chargers: {
    labelTranslationKey: 'side-menu.chargers',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.Chargers],
    permissions: [PermissionStoreModelEnum.ViewAnySites, PermissionStoreModelEnum.ViewOwnSites],
    requiresGlobalChargersEnabled: true,
  },
  'charger-sessions': {
    labelTranslationKey: 'global-chargers.sessions.title',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.Chargers, RouteSegment.ChargerSessions],
    permissions: [PermissionStoreModelEnum.ViewAnySites, PermissionStoreModelEnum.ViewOwnSites],
    requiresGlobalChargersEnabled: true,
  },
  'charger-live-issues': {
    labelTranslationKey: 'global-chargers.live-issues.title',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.Chargers, RouteSegment.LiveIssues],
    permissions: [PermissionStoreModelEnum.ViewAnySites, PermissionStoreModelEnum.ViewOwnSites],
    additionalPermissionGroups: [[PermissionStoreModelEnum.ViewAnyChargerLiveIssues, PermissionStoreModelEnum.ViewOwnChargerLiveIssues]],
    requiresGlobalChargersEnabled: true,
  },
  'charger-infrastructure-health': {
    labelTranslationKey: 'global-chargers.infrastructure-health.title',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.Chargers, RouteSegment.InfrastructureHealth],
    permissions: [PermissionStoreModelEnum.ViewAnySites, PermissionStoreModelEnum.ViewOwnSites],
    additionalPermissionGroups: [[PermissionStoreModelEnum.ViewAnyChargerUptime, PermissionStoreModelEnum.ViewOwnChargerUptime]],
    requiresGlobalChargersEnabled: true,
  },
  sharing: {
    labelTranslationKey: 'side-menu.sharing',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.Sharing, RouteSegment.Analytics],
    permissions: [PermissionStoreModelEnum.ViewAnyAnalytics, PermissionStoreModelEnum.ViewOwnAnalytics],
    featureFlags: [FeatureFlags.Sharing],
  },
  roaming: {
    labelTranslationKey: 'side-menu.roaming',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.Roaming, RouteSegment.Analytics],
    permissions: [PermissionStoreModelEnum.ViewAnyAnalytics, PermissionStoreModelEnum.ViewOwnAnalytics],
    featureFlags: [FeatureFlags.Roaming],
  },
  flexibility: {
    labelTranslationKey: 'side-menu.flexibility',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.Flexibility],
    permissions: [PermissionStoreModelEnum.ViewAnyEnergyTradingDashboard, PermissionStoreModelEnum.ViewOwnEnergyTradingDashboard],
    featureFlags: [FeatureFlags.Flexibility],
  },
  'flexibility-drill-down': {
    labelTranslationKey: 'quick-navigation.flexibility-drill-down',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.Flexibility, RouteSegment.DrillDown],
    permissions: [PermissionStoreModelEnum.ViewAnyEnergyTradingDashboard, PermissionStoreModelEnum.ViewOwnEnergyTradingDashboard],
    featureFlags: [FeatureFlags.Flexibility],
  },
  'energy-trading': {
    labelTranslationKey: 'side-menu.energy-trading',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.EnergyTrading],
    permissions: [PermissionStoreModelEnum.ViewAnyGbTradingPositionCalculation],
  },
  'energy-trading-flexibility-forecast': {
    labelTranslationKey: 'energy-trading.flexibility-forecast',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.EnergyTrading, RouteSegment.FlexibilityForecast],
    permissions: [PermissionStoreModelEnum.ViewAnyGbTradingPositionCalculation],
  },
  'energy-trading-ev-usage-forecast': {
    labelTranslationKey: 'energy-trading.ev-usage-forecast',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.EnergyTrading, RouteSegment.EvUsageForecast],
    permissions: [PermissionStoreModelEnum.ViewAnyGbTradingPositionCalculation],
  },
  'energy-trading-arrival-times': {
    labelTranslationKey: 'energy-trading.arrival-times',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.EnergyTrading, RouteSegment.ArrivalTimes],
    permissions: [PermissionStoreModelEnum.ViewAnyGbTradingPositionCalculation],
  },
  'energy-trading-fast-slow-curve': {
    labelTranslationKey: 'energy-trading.fast-slow-curve',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.EnergyTrading, RouteSegment.FastSlowCurve],
    permissions: [PermissionStoreModelEnum.ViewAnyGbTradingPositionCalculation],
  },
  'energy-trading-evse-forecast': {
    labelTranslationKey: 'energy-trading.evse-forecast',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.EnergyTrading, RouteSegment.EvseForecast],
    permissions: [PermissionStoreModelEnum.ViewAnyGbTradingPositionCalculation],
  },
  'energy-trading-baseline': {
    labelTranslationKey: 'energy-trading.baseline',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.EnergyTrading, RouteSegment.Baseline],
    permissions: [PermissionStoreModelEnum.ViewAnyGbTradingPositionCalculation],
  },
  'energy-trading-setpoints-trading-flags': {
    labelTranslationKey: 'energy-trading.setpoints-trading-flags',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.EnergyTrading, RouteSegment.SetpointsTradingFlags],
    permissions: [PermissionStoreModelEnum.ViewAnyGbTradingPositionCalculation],
  },
  'energy-trading-gsp-harmonisation': {
    labelTranslationKey: 'energy-trading.gsp-harmonisation',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.EnergyTrading, RouteSegment.GspHarmonisation],
    permissions: [PermissionStoreModelEnum.ViewAnyGbTradingPositionCalculation],
  },
  'energy-trading-small-bess': {
    labelTranslationKey: 'energy-trading.small-bess',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.EnergyTrading, RouteSegment.SmallBess],
    permissions: [PermissionStoreModelEnum.ViewAnyGbTradingPositionCalculation],
  },
  'energy-trading-supply-volume': {
    labelTranslationKey: 'energy-trading.supply-volume',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.EnergyTrading, RouteSegment.SupplyVolume],
    permissions: [PermissionStoreModelEnum.ViewAnyGbTradingPositionCalculation],
  },
  'energy-trading-expected-volume': {
    labelTranslationKey: 'energy-trading.expected-volume',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.EnergyTrading, RouteSegment.ExpectedVolume],
    permissions: [PermissionStoreModelEnum.ViewAnyGbTradingPositionCalculation],
  },
  'energy-trading-day-ahead-trades': {
    labelTranslationKey: 'energy-trading.day-ahead-trades',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.EnergyTrading, RouteSegment.DayAheadTrades],
    permissions: [PermissionStoreModelEnum.ViewAnyGbTradingPositionCalculation],
  },
  'energy-trading-calculation-settings': {
    labelTranslationKey: 'energy-trading.calculation-settings-title',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.EnergyTrading, RouteSegment.TradingKnobs, RouteSegment.CalculationSettings],
    permissions: [PermissionStoreModelEnum.ViewAnyGbTradingPositionCalculation],
  },
  'energy-trading-baseline-adjustment': {
    labelTranslationKey: 'energy-trading.baseline-adjustment',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.EnergyTrading, RouteSegment.TradingKnobs, RouteSegment.BaselineAdjustment],
    permissions: [PermissionStoreModelEnum.ViewAnyGbTradingPositionCalculation],
  },
  workflow: {
    labelTranslationKey: 'side-menu.workflow-management',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.WorkflowManagement],
    permissions: [PermissionStoreModelEnum.ViewWorkflow],
  },
  'workflow-data-flows': {
    labelTranslationKey: 'quick-navigation.data-flows',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.WorkflowManagement, RouteSegment.DataFlows],
    permissions: [PermissionStoreModelEnum.ViewWorkflow, PermissionStoreModelEnum.ViewDataflow],
    additionalPermissionGroups: [[PermissionStoreModelEnum.ViewWorkflow], [PermissionStoreModelEnum.ViewDataflow]],
  },
  preferences: { labelTranslationKey: 'preferences.title', scope: 'global', segments: [RouteSegment.Root, RouteSegment.Preferences] },
  'settings-users': {
    labelTranslationKey: 'side-menu.users',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.Settings, RouteSegment.Users],
    permissions: [PermissionStoreModelEnum.ViewAnyUsers, PermissionStoreModelEnum.ViewOwnUsers],
  },
  'settings-audit-logs': {
    labelTranslationKey: 'side-menu.audit-logs',
    scope: 'global',
    segments: [RouteSegment.Root, RouteSegment.Settings, RouteSegment.AuditLogs],
    permissions: [PermissionStoreModelEnum.ViewAnyAuditLogs, PermissionStoreModelEnum.ViewOwnAuditLogs],
  },
  'site-overview': { labelTranslationKey: 'single-site.overview.title', scope: 'site', segments: [RouteSegment.Overview] },
  'site-vehicles': {
    labelTranslationKey: 'single-site.vehicles.title',
    scope: 'site',
    segments: [RouteSegment.Vehicles],
    permissions: [PermissionStoreModelEnum.ViewAnyVehicles, PermissionStoreModelEnum.ViewOwnVehicles],
    requiresFleetSite: true,
  },
  'site-chargers': { labelTranslationKey: 'single-site.chargers.title', scope: 'site', segments: [RouteSegment.Chargers] },
  'site-charger-sessions': {
    labelTranslationKey: 'global-chargers.sessions.title',
    scope: 'site',
    segments: [RouteSegment.Chargers, RouteSegment.ChargerSessions],
    requiresDesktop: true,
  },
  'site-live-issues': {
    labelTranslationKey: 'global-chargers.live-issues.title',
    scope: 'site',
    segments: [RouteSegment.Chargers, RouteSegment.LiveIssues],
    permissions: [PermissionStoreModelEnum.ViewAnyChargerLiveIssues, PermissionStoreModelEnum.ViewOwnChargerLiveIssues],
    requiresDesktop: true,
  },
  'site-infrastructure-health': {
    labelTranslationKey: 'global-chargers.infrastructure-health.title',
    scope: 'site',
    segments: [RouteSegment.Chargers, RouteSegment.InfrastructureHealth],
    permissions: [PermissionStoreModelEnum.ViewAnyChargerUptime, PermissionStoreModelEnum.ViewOwnChargerUptime],
    requiresDesktop: true,
  },
  'site-analytics': {
    labelTranslationKey: 'single-site.analytics.title',
    scope: 'site',
    segments: [RouteSegment.Analytics],
    permissions: [PermissionStoreModelEnum.ViewAnyAnalytics, PermissionStoreModelEnum.ViewOwnAnalytics],
  },
  'site-analytics-graph': {
    labelTranslationKey: 'quick-navigation.analytics-graph',
    scope: 'site',
    segments: [RouteSegment.Analytics, RouteSegment.Graph],
    permissions: [PermissionStoreModelEnum.ViewAnyAnalytics, PermissionStoreModelEnum.ViewOwnAnalytics],
  },
  'site-alerting': { labelTranslationKey: 'single-site.alerting.title', scope: 'site', segments: [RouteSegment.Alerting] },
  'site-predictive-overview': {
    labelTranslationKey: 'single-site.predictive-infrastructure.tab-title',
    scope: 'site',
    segments: [RouteSegment.PredictiveInfrastructure, RouteSegment.Overview],
    permissions: [PermissionStoreModelEnum.ViewAnyPredictiveInfraOverview, PermissionStoreModelEnum.ViewOwnPredictiveInfraOverview],
    featureFlags: [FeatureFlags.PredictiveAnalytics],
  },
  'site-chargers-drill-down': {
    labelTranslationKey: 'quick-navigation.chargers-drill-down',
    scope: 'site',
    segments: [RouteSegment.PredictiveInfrastructure, RouteSegment.ChargersDrillDown],
    permissions: [
      PermissionStoreModelEnum.ViewAnyPredictiveInfraChargerPerformance,
      PermissionStoreModelEnum.ViewOwnPredictiveInfraChargerPerformance,
    ],
    featureFlags: [FeatureFlags.PredictiveAnalytics],
  },
  'site-predictive-fleet': {
    labelTranslationKey: 'single-site.predictive-infrastructure.fleet',
    scope: 'site',
    segments: [RouteSegment.PredictiveInfrastructure, RouteSegment.Fleet],
    permissions: [
      PermissionStoreModelEnum.ViewAnyPredictiveInfraVehicleHealth,
      PermissionStoreModelEnum.ViewOwnPredictiveInfraVehicleHealth,
    ],
    requiresFleetSite: true,
    featureFlags: [FeatureFlags.PredictiveAnalytics],
  },
  'site-predictive-sessions-overview': {
    labelTranslationKey: 'single-site.predictive-infrastructure.sessions-overview',
    scope: 'site',
    segments: [RouteSegment.PredictiveInfrastructure, RouteSegment.SessionsOverview],
    permissions: [PermissionStoreModelEnum.ViewAnyPredictiveInfraOverview, PermissionStoreModelEnum.ViewOwnPredictiveInfraOverview],
    featureFlags: [FeatureFlags.PredictiveAnalytics],
  },
  'site-vehicle-trips': {
    labelTranslationKey: 'vehicles.trips.tab-title',
    scope: 'site',
    segments: [RouteSegment.Vehicles, RouteSegment.Trip],
    permissions: [PermissionStoreModelEnum.ViewAnyVehicles, PermissionStoreModelEnum.ViewOwnVehicles],
    requiresFleetSite: true,
    requiresTelemetryFeaturesEnabled: true,
  },
  'site-vehicle-issues': {
    labelTranslationKey: 'quick-navigation.vehicle-issues',
    scope: 'site',
    segments: [RouteSegment.Vehicles, RouteSegment.Issues],
    permissions: [PermissionStoreModelEnum.ViewAnyVehicles, PermissionStoreModelEnum.ViewOwnVehicles],
    requiresFleetSite: true,
  },
  'site-vehicle-departure-soc': {
    labelTranslationKey: 'quick-navigation.vehicle-departure-soc',
    scope: 'site',
    segments: [RouteSegment.Vehicles, RouteSegment.DepartureSoc],
    permissions: [PermissionStoreModelEnum.ViewAnyVehicles, PermissionStoreModelEnum.ViewOwnVehicles],
    requiresFleetSite: true,
    requiresTelemetryFeaturesEnabled: true,
  },
  kiosk: {
    labelTranslationKey: 'quick-navigation.kiosk',
    scope: 'site',
    segments: [RouteSegment.Kiosk],
    permissions: [
      PermissionStoreModelEnum.ViewAnyKiosk,
      PermissionStoreModelEnum.ViewOwnKiosk,
      PermissionStoreModelEnum.ViewAnySites,
      PermissionStoreModelEnum.ViewOwnSites,
    ],
    requiresFleetSite: true,
    requiresGridViewEnabled: true,
  },
  'kiosk-new': {
    labelTranslationKey: 'quick-navigation.kiosk-new',
    scope: 'site',
    segments: [RouteSegment.KioskNew],
    permissions: [
      PermissionStoreModelEnum.ViewAnyKiosk,
      PermissionStoreModelEnum.ViewOwnKiosk,
      PermissionStoreModelEnum.ViewAnySites,
      PermissionStoreModelEnum.ViewOwnSites,
    ],
    requiresFleetSite: true,
    requiresGridViewEnabled: true,
    requiresOptimoTenant: true,
  },
} as const satisfies Record<string, QuickNavigationRouteDefinition>;

export type QuickNavigationRouteId = keyof typeof quickNavigationRoutes;
export type QuickNavigationRoute = QuickNavigationRouteDefinition;

export const quickNavigationRouteEntries = Object.entries(quickNavigationRoutes) as [QuickNavigationRouteId, QuickNavigationRoute][];

export const getQuickNavigationRoute = <T extends QuickNavigationRouteId>(id: T): (typeof quickNavigationRoutes)[T] => {
  return quickNavigationRoutes[id];
};
